import React from 'react';
import { useLocation } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import Navbar from '@/components/layout/Navbar';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { FullPageSkeleton } from '@/components/feedback/unified-skeleton';
import { useAuth } from '@/context/AuthContext';
import { useBusinessContext } from '@/context/BusinessContext';
import {
  SidebarProvider,
  Sidebar,
  SidebarContent as SidebarUiContent,
  SidebarFooter as SidebarUiFooter,
  DashboardSidebarNavigation,
  SidebarInset,
  SidebarMenu,
  SidebarModeToggle,
  SidebarSettingsButtons,
} from '@/components/ui/sidebar';
import { MobileAIChatOverlay, AIRightSidebar, AIFloatingBubble, AIFloatingWindow } from '@/components/ai-chat';
import { MobileAIChatProvider } from '@/context/MobileAIChatContext';
import { BottomNavProvider } from '@/context/BottomNavContext';
import { AIViewProvider } from '@/context/AIViewContext';
import { AccountSettingsSidebarNav } from '@/components/settings/AccountSettingsSidebarNav';
import { BusinessSettingsSidebarNav } from '@/components/settings/BusinessSettingsSidebarNav';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { CommandPalette, DefaultCommands } from '@/components/command-palette';
import { type DashboardType, getDashboardTypeFromPath } from '@/lib/constants/dashboard';
import { useMigrateLocalDrafts } from '@/hooks/business/useWizardDrafts';

// Logo icon component to match sidebar navigation pattern
const LogoIcon = () => (
  <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="size-6 shrink-0" />
);

// Pages that show navbar but no sidebar
const isNavbarOnlyPage = (pathname: string): boolean => {
  return pathname === '/create-business';
};

interface GlobalLayoutProps {
  children: React.ReactNode;
}

export const GlobalLayout: React.FC<GlobalLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // One-time migration of localStorage wizard drafts to Supabase
  useMigrateLocalDrafts(user?.id);

  // Derive settings state from URL
  const isAccountSettingsOpen = location.pathname.startsWith('/settings/account');
  const isBusinessSettingsOpen = location.pathname.startsWith('/settings/business/');
  const {
    businesses,
    loadingBusinesses,
    selectedBusinessId,
    switchBusiness,
  } = useBusinessContext();

  // Get dashboard tab context for navigation state
  const {
    businessDashboardTab,
    setBusinessDashboardTab,
    employeeDashboardTab,
    setEmployeeDashboardTab,
    employeePortalTab,
    setEmployeePortalTab,
    franchiseDashboardTab,
    setFranchiseDashboardTab
  } = useDashboardTab();
  const { currentPlan } = useSubscription();
  const showAssistant = currentPlan?.name !== 'free';

  const dashboardType = getDashboardTypeFromPath(location.pathname);
  const isOnDashboard = dashboardType !== null;
  const showNavbarOnly = isNavbarOnlyPage(location.pathname) && !!user;

  // Track if we've completed initial auth to prevent skeleton flash on tab switch
  const hasInitializedRef = React.useRef(false);
  if (!authLoading && user) {
    hasInitializedRef.current = true;
  }

  // Show sidebar when: account/business settings is open OR user is on a dashboard page
  // Use ref to "remember" if we've shown sidebar to prevent layout flickering on tab switch
  const hasShownSidebarRef = React.useRef(false);
  const shouldShowSidebar = isAccountSettingsOpen || isBusinessSettingsOpen || (isOnDashboard && !!user);

  // Once we've shown the sidebar for a dashboard, keep showing it even if user temporarily becomes null
  if (shouldShowSidebar) {
    hasShownSidebarRef.current = true;
  }
  const showSidebar = shouldShowSidebar || (isOnDashboard && hasShownSidebarRef.current);

  // Show full page skeleton ONLY during initial auth loading, not on subsequent re-renders
  // This prevents the layout from unmounting/remounting on tab switch
  if (authLoading && isOnDashboard && !hasInitializedRef.current) {
    return <FullPageSkeleton />;
  }

  // Note: Business data comes from useBusinessContext() above
  // The context handles conditional fetching internally

  // Get the appropriate activeView and setActiveView based on dashboard type
  const getNavigation = () => {
    switch (dashboardType) {
      case 'business':
        return { activeView: businessDashboardTab, setActiveView: setBusinessDashboardTab };
      case 'employee':
        return { activeView: employeeDashboardTab, setActiveView: setEmployeeDashboardTab };
      case 'employee-portal':
        return { activeView: employeePortalTab, setActiveView: setEmployeePortalTab };
      case 'franchise':
        return { activeView: franchiseDashboardTab, setActiveView: setFranchiseDashboardTab };
      default:
        return { activeView: 'dashboard', setActiveView: () => {} };
    }
  };

  const { activeView, setActiveView } = getNavigation();

  // For pages without sidebar (public pages when settings is closed)
  if (!showSidebar) {
    // Navbar-only pages: show navbar with business select but hide navigation buttons and sidebar
    if (showNavbarOnly) {
      return (
        <BottomNavProvider>
          <MobileAIChatProvider>
            <AIViewProvider>
              <SidebarProvider>
                <div className="flex flex-col w-full min-h-screen bg-background">
                  <Navbar
                    businesses={businesses}
                    selectedBusinessId={selectedBusinessId}
                    onBusinessChange={switchBusiness}
                    loadingBusinesses={loadingBusinesses}
                    hideBusinessNavigation
                    forceShowBusinessSelect
                  />
                  <div className="flex-1">
                    {children}
                  </div>
                </div>
                <MobileBottomNav />
                {showAssistant && <MobileAIChatOverlay businessId={selectedBusinessId} />}
              </SidebarProvider>
            </AIViewProvider>
          </MobileAIChatProvider>
        </BottomNavProvider>
      );
    }

    // Pages that manage their own full-screen layout (no spacer needed)
    const isFullScreenPage = location.pathname === '/auth' || location.pathname === '/submit-review';

    return (
      <BottomNavProvider>
        <MobileAIChatProvider>
          <AIViewProvider>
            {children}
            {/* Mobile bottom nav spacer - ensures content isn't hidden behind fixed nav */}
            {!isFullScreenPage && <div className="h-24 md:hidden" />}
            <MobileBottomNav />
            {showAssistant && <MobileAIChatOverlay businessId={selectedBusinessId} />}
          </AIViewProvider>
        </MobileAIChatProvider>
      </BottomNavProvider>
    );
  }

  return (
    <BottomNavProvider>
      <MobileAIChatProvider>
        <AIViewProvider>
          <SidebarProvider forceExpanded={isAccountSettingsOpen || isBusinessSettingsOpen}>
            {/* Command Palette - global keyboard shortcuts UI */}
            <CommandPalette />
            <DefaultCommands />
            <div className="w-full bg-background flex">
              <Sidebar side="left" variant="sidebar" collapsible="icon">
              <SidebarUiContent className="p-0">
                {/* Logo section with static positioning - stays collapsed in expand-on-hover mode */}
                <div className="pl-3 pt-2.5 group-data-[display-mode=expand-on-hover]:w-[--sidebar-width-icon] group-data-[display-mode=expand-on-hover]:overflow-hidden group-data-[display-mode=expand-on-hover]:flex-shrink-0">
                  <div className="flex items-center gap-2 h-8">
                    <LogoIcon />
                    <span className="text-sm font-medium text-sidebar-foreground group-data-[collapsible=icon]:opacity-0 group-data-[display-mode=expand-on-hover]:opacity-0 transition-opacity duration-200">
                      TagInn
                    </span>
                  </div>
                </div>

                {/* Conditional Navigation - Priority: Account > Business > Dashboard */}
                {isAccountSettingsOpen ? (
                  <AccountSettingsSidebarNav />
                ) : isBusinessSettingsOpen ? (
                  <BusinessSettingsSidebarNav />
                ) : (
                  dashboardType && (selectedBusinessId || dashboardType === 'employee-portal' || dashboardType === 'franchise') && (
                    <DashboardSidebarNavigation
                      activeView={activeView}
                      setActiveView={setActiveView}
                      dashboardType={dashboardType}
                    />
                  )
                )}
              </SidebarUiContent>

              <SidebarUiFooter>
                <SidebarMenu>
                  <SidebarSettingsButtons />
                  <SidebarModeToggle />
                </SidebarMenu>
              </SidebarUiFooter>
            </Sidebar>

            <div className="flex flex-col flex-1" style={{ minWidth: 0 }}>
              <Navbar
                businesses={businesses}
                selectedBusinessId={selectedBusinessId}
                onBusinessChange={switchBusiness}
                loadingBusinesses={loadingBusinesses}
                isPortfolioView={dashboardType === 'franchise'}
              />
              <SidebarInset className="flex-1">
                <ScrollArea className="flex-1 w-full">
                  {/* Sole page gutter for dashboard and settings tabs. Tab roots do not add their own p-4. */}
                  <div className="mx-auto p-4 w-full">
                    {children}
                  </div>
                  {/* Mobile bottom nav spacer - ensures content isn't hidden behind fixed nav */}
                  {/* Not shown in employee portal - navigation handled by sidebar sheet */}
                  {dashboardType !== 'employee-portal' && <div className="h-24 md:hidden" />}
                  {/* Desktop AI bubble spacer - ensures content isn't hidden behind floating input */}
                  {showAssistant && dashboardType !== 'employee-portal' && <div className="hidden md:block h-24" />}
                </ScrollArea>
              </SidebarInset>
            </div>

            {/* Right AI Sidebar - inside flex to push content */}
            {/* Hidden in employee portal, account settings, and business settings */}
            {showAssistant && !isAccountSettingsOpen && !isBusinessSettingsOpen && dashboardType !== 'employee-portal' && (
              <AIRightSidebar businessId={selectedBusinessId} />
            )}
          </div>

            {/* Desktop AI Chat - floating bubble + floating window (overlays) */}
            {/* Hidden in employee portal, account settings, business settings, and on the free plan */}
            {showAssistant && !isAccountSettingsOpen && !isBusinessSettingsOpen && dashboardType !== 'employee-portal' && (
              <>
                <AIFloatingBubble />
                <AIFloatingWindow businessId={selectedBusinessId} />
              </>
            )}

            {/* Mobile Navigation Components */}
            <MobileBottomNav />
            {showAssistant && <MobileAIChatOverlay businessId={selectedBusinessId} />}
          </SidebarProvider>
        </AIViewProvider>
      </MobileAIChatProvider>
    </BottomNavProvider>
  );
};

export default GlobalLayout;
